import { connect } from "react-redux";

import * as actions from "../../store/actions/index";

import { formatDateWithTime, withRouter } from "../../shared/utility";
import { MixedCartonHistoryViewModel } from "../../viewmodels/traceability/MixedCartonHistoryViewModel";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import MixedCartonHistoryTable from "./mixedCartonHistory/MixedCartonHistoryTable";
import MixedCartonSourceItemsTable from "./mixedCartonHistory/MixedCartonSourceItemsTable";
import EmptyModal from "../../components/modals/EmptyModal";
import { Skeleton } from "primereact/skeleton";
import HistoryRecordSummary, { HistoryDetail } from "../../components/traceability/HistoryRecordSummary";

export function MixedCartonHistoryScreen(props) {
  const { logout } = props;
  const {
    mixedCarton,
    getMixedCartonHistory,
    loading,
    emptyModal,
    setEmptyModelOpen,
  } = MixedCartonHistoryViewModel(logout);

  const [searchParams] = useSearchParams();
  const requestedBarcode = searchParams.get("barcode")?.trim() ?? "";
  const requestedCartonId = searchParams.get("mixed_carton_id");
  const searchRef = useRef();
  const lastAutomaticLookup = useRef("");

  useEffect(() => {
    const lookupKey = JSON.stringify([requestedBarcode, requestedCartonId]);
    if (!requestedBarcode || lastAutomaticLookup.current === lookupKey) {
      return;
    }

    lastAutomaticLookup.current = lookupKey;
    if (searchRef.current) searchRef.current.value = requestedBarcode;
    void getMixedCartonHistory(requestedBarcode, requestedCartonId ?? undefined);
  }, [getMixedCartonHistory, requestedBarcode, requestedCartonId]);

  const searchHistory = () => {
    const barcode = searchRef.current.value.trim();
    // A different manual search must not keep the linked carton's identity.
    const cartonId = barcode === requestedBarcode ? requestedCartonId : null;
    void getMixedCartonHistory(barcode, cartonId ?? undefined);
  };

  return (
    <div>
      <div className="mt-2 space-y-4 px-4 lg:px-8">
        <div className="rounded-xl border border-slate-200 bg-white px-4 py-4 sm:px-5">
          <div className="sm:flex-auto">
            <h1 className="text-xl font-semibold leading-7 text-slate-800">
              Mixed carton history
            </h1>
            <label htmlFor="searchMixedCarton" className="mb-1.5 mt-4 block text-sm font-medium text-slate-700">Mixed carton barcode</label>
            <div className="flex max-w-2xl rounded-md">
              <div className="relative flex items-stretch flex-grow focus-within:z-10">
                <input
                  type="search"
                  name="searchMixedCarton"
                  id="searchMixedCarton"
                  className="block min-h-[44px] w-full min-w-0 rounded-l-md border border-slate-300 px-3 text-sm text-slate-800 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:min-h-[40px]"
                  placeholder="Search by mixed carton barcode"
                  ref={searchRef}
                  defaultValue={requestedBarcode}
                  onKeyDown={(e) => {
                    if (e.key === "Enter")
                      searchHistory();
                  }}
                />
              </div>
              <button
                type="button"
                aria-label="Search history"
                className="-ml-px inline-flex min-w-[44px] items-center justify-center rounded-r-md border border-slate-300 bg-slate-50 px-3 text-slate-600 hover:bg-slate-100 focus-visible:z-10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500"
                onClick={searchHistory}
              >
                <MagnifyingGlassIcon
                  className="h-5 w-5 text-gray-400"
                  aria-hidden="true"
                />
              </button>
            </div>
          </div>
        </div>
        {loading ? (
          <div className="space-y-2">
            <Skeleton width="100%" height="8rem" borderRadius="16px"></Skeleton>
            <Skeleton width="100%" height="8rem" borderRadius="16px"></Skeleton>
          </div>
        ) : mixedCarton.barcode ? (
          <div>
            <HistoryRecordSummary
              title={mixedCarton.order?.invoice_number ? `Invoice ${mixedCarton.order.invoice_number}` : "Mixed carton details"}
              status={mixedCarton.is_returned === 1 ? "Returned" : "Shipped"}
              barcode={mixedCarton.barcode}
            >
              <HistoryDetail label="Customer">{mixedCarton.order?.customer?.name}</HistoryDetail>
              <HistoryDetail label="Created by">{mixedCarton.shipped_by_name}</HistoryDetail>
              <HistoryDetail label="Created date">{formatDateWithTime(mixedCarton.created_at)}</HistoryDetail>
              <HistoryDetail label="Subtotal">{mixedCarton.subtotal_price == null ? "—" : `$${mixedCarton.subtotal_price}`}</HistoryDetail>
              <HistoryDetail label="Tax">{mixedCarton.tax_amount == null ? "—" : `$${mixedCarton.tax_amount}`}</HistoryDetail>
              <HistoryDetail label="Total price">{mixedCarton.total_price == null ? "—" : `$${mixedCarton.total_price}`}</HistoryDetail>
            </HistoryRecordSummary>
            <MixedCartonHistoryTable historyData={mixedCarton.barcode_histories} />
              <div className="mt-4">
                <h2 className="text-xl font-semibold leading-7 text-slate-800 mb-4 px-4">
                  Items in mixed carton
                </h2>
                <MixedCartonSourceItemsTable sourceItemsData={mixedCarton.source_items} />
              </div>
          </div>
        ) : null}
      </div>

      <EmptyModal
        childComponent={emptyModal.childComponent}
        open={emptyModal.open}
        setOpen={setEmptyModelOpen}
        size={emptyModal.size}
      />
    </div>
  );
}

const mapStateToProps = (state) => {
  return {};
};

const mapDispatchToProps = (dispatch) => {
  return {
    logout: () => dispatch(actions.logout()),
  };
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(MixedCartonHistoryScreen)
);
